import * as React from 'react';
import { ChevronDown, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import {
  PHONE_COUNTRIES,
  type PhoneCountry,
  buildPhoneExample,
  findPhoneCountryByIso,
  formatNationalNumber,
} from '@/shared/lib/phone-countries';
import { cn } from '@/lib/utils';

interface PhoneInputProps {
  countryIso: string;
  nationalDigits: string;
  onCountryChange: (country: PhoneCountry) => void;
  onNationalDigitsChange: (digits: string) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
  invalid?: boolean;
}

export function PhoneInput({
  countryIso,
  nationalDigits,
  onCountryChange,
  onNationalDigitsChange,
  disabled,
  id,
  className,
  invalid,
}: PhoneInputProps) {
  const country = findPhoneCountryByIso(countryIso) ?? PHONE_COUNTRIES[0];
  const display = formatNationalNumber(nationalDigits, country.mask);
  const placeholder = buildPhoneExample(country);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/\D/g, '').slice(0, country.maxDigits);
    onNationalDigitsChange(cleaned);
  };

  const handleSelectCountry = (next: PhoneCountry) => {
    if (next.iso === country.iso) return;
    onCountryChange(next);
    if (nationalDigits.length > next.maxDigits) {
      onNationalDigitsChange(nationalDigits.slice(0, next.maxDigits));
    }
  };

  return (
    <div
      className={cn(
        'flex h-10 w-full overflow-hidden rounded-md border bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
        invalid ? 'border-destructive' : 'border-input',
        disabled && 'opacity-50',
        className,
      )}
    >
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={disabled}
          className="flex shrink-0 items-center gap-1 border-r border-input bg-background px-3 text-sm outline-none transition-colors hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Select country code"
        >
          <span className="text-base leading-none">{country.flag}</span>
          <span className="font-medium">+{country.dialCode}</span>
          <ChevronDown className="ml-0.5 h-3.5 w-3.5 opacity-60" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="max-h-72 w-72 overflow-y-auto"
          sideOffset={6}
        >
          {PHONE_COUNTRIES.map((c) => {
            const selected = c.iso === country.iso;
            return (
              <DropdownMenuItem
                key={c.iso}
                onSelect={() => handleSelectCountry(c)}
                className="flex items-center gap-2 pr-2"
              >
                <span className="text-base leading-none">{c.flag}</span>
                <span className="flex-1 truncate">{c.name}</span>
                <span className="text-muted-foreground">+{c.dialCode}</span>
                {selected && <Check className="h-3.5 w-3.5 text-primary" />}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <Input
        id={id}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        placeholder={placeholder}
        value={display}
        onChange={handleInputChange}
        disabled={disabled}
        className="h-full flex-1 border-0 bg-transparent px-3 focus-visible:ring-0 focus-visible:ring-offset-0"
      />
    </div>
  );
}
